import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';

const { 
  FiFileText, FiShield, FiCheckCircle, FiXCircle, FiAlertCircle, 
  FiMessageSquare, FiStar, FiDownload, FiEye, FiActivity, FiSearch,
  FiFilter, FiMoreVertical, FiLock, FiClock, FiScissors, FiUploadCloud,
  FiLayers, FiExternalLink, FiCopy, FiEdit3, FiPlus, FiTrash2, FiTag
} = FiIcons;

export default function EvidenceReviewWorkspace({ reportId, currentUser }) {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showRedactionModal, setShowRedactionModal] = useState(false);
  const [previewMode, setPreviewMode] = useState('original'); // 'original' or 'redacted'

  // Annotation state
  const [annotations, setAnnotations] = useState([]);
  const [activeTab, setActiveTab] = useState('review'); // 'review' or 'annotations'
  const [newAnnotation, setNewAnnotation] = useState({ content: '', type: 'observation' });

  useEffect(() => {
    fetchEvidence();
  }, [reportId]);

  useEffect(() => {
    if (selectedItem) {
      fetchAnnotations(selectedItem.id);
      setPreviewMode(selectedItem.redacted_file_url ? 'redacted' : 'original');
    }
  }, [selectedItem]);

  const fetchEvidence = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('evidence_items_1788616000000')
        .select('*')
        .eq('report_id', reportId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEvidence(data || []);
      if (data?.length > 0 && !selectedItem) {
        setSelectedItem(data[0]);
      }
    } catch (err) {
      console.error('Error fetching evidence:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnotations = async (itemId) => {
    try {
      const { data, error } = await supabase
        .from('evidence_annotations_1788628000000')
        .select('*')
        .eq('item_id', itemId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setAnnotations(data || []);
    } catch (err) {
      console.error('Error fetching annotations:', err);
    }
  };

  const handleUpdateReview = async (itemId, updates) => {
    try {
      setIsSaving(true);
      const payload = {
        ...updates,
        reviewed_at: new Date().toISOString(),
        reviewed_by: currentUser?.name || 'System'
      };

      const { error } = await supabase
        .from('evidence_items_1788616000000')
        .update(payload)
        .eq('id', itemId);

      if (error) throw error;

      // Log the review action
      await supabase.from('evidence_logs_1788616000000').insert([{
        item_id: itemId,
        action: 'reviewed',
        performed_by: currentUser?.name || 'System',
        notes: `Vurdering oppdatert: ${updates.review_status || 'notater endret'}`
      }]);

      setEvidence(prev => prev.map(item => 
        item.id === itemId ? { ...item, ...payload } : item
      ));
      if (selectedItem?.id === itemId) {
        setSelectedItem(prev => ({ ...prev, ...payload }));
      }
    } catch (err) {
      console.error('Error updating review:', err);
      alert('Kunne ikke lagre vurdering.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAnnotation = async () => {
    if (!newAnnotation.content.trim() || !selectedItem) return;

    try {
      setIsSaving(true);
      const payload = {
        item_id: selectedItem.id,
        content: newAnnotation.content,
        type: newAnnotation.type,
        author_name: currentUser?.name || 'System',
        author_id: currentUser?.id || 'system'
      };

      const { data, error } = await supabase
        .from('evidence_annotations_1788628000000')
        .insert([payload])
        .select();

      if (error) throw error;

      setAnnotations(prev => [...prev, data[0]]);
      setNewAnnotation({ content: '', type: 'observation' });
      
      // Log annotation addition
      await supabase.from('evidence_logs_1788616000000').insert([{
        item_id: selectedItem.id,
        action: 'annotated',
        performed_by: currentUser?.name || 'System',
        notes: `Ny annotering lagt til (${payload.type})`
      }]);
    } catch (err) {
      console.error('Error adding annotation:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAnnotation = async (annotationId) => {
    try {
      const { error } = await supabase
        .from('evidence_annotations_1788628000000')
        .delete()
        .eq('id', annotationId);

      if (error) throw error;
      setAnnotations(prev => prev.filter(a => a.id !== annotationId));
    } catch (err) {
      console.error('Error deleting annotation:', err);
    }
  };

  const handleRedactionComplete = async (itemId, redactedUrl, notes) => {
    try {
      setIsSaving(true);
      const payload = {
        redaction_status: 'completed',
        redacted_file_url: redactedUrl,
        redaction_notes: notes,
        redacted_at: new Date().toISOString(),
        redacted_by: currentUser?.name || 'System'
      };

      const { error } = await supabase
        .from('evidence_items_1788616000000')
        .update(payload)
        .eq('id', itemId);

      if (error) throw error;

      // Log the redaction
      await supabase.from('evidence_logs_1788616000000').insert([{
        item_id: itemId,
        action: 'redacted',
        performed_by: currentUser?.name || 'System',
        notes: `Sladdet versjon lastet opp: ${notes}`
      }]);

      setEvidence(prev => prev.map(item => 
        item.id === itemId ? { ...item, ...payload } : item
      ));
      if (selectedItem?.id === itemId) {
        setSelectedItem(prev => ({ ...prev, ...payload }));
        setPreviewMode('redacted');
      }
      setShowRedactionModal(false);
    } catch (err) {
      console.error('Error completing redaction:', err);
      alert('Kunne ikke lagre sladdet versjon.');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredEvidence = evidence.filter(item => {
    const matchesFilter = filter === 'all' || item.review_status === filter;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         item.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'relevant': return <SafeIcon icon={FiCheckCircle} className="text-green-500" />;
      case 'irrelevant': return <SafeIcon icon={FiXCircle} className="text-red-500" />;
      case 'needs_clarification': return <SafeIcon icon={FiAlertCircle} className="text-amber-500" />;
      default: return <SafeIcon icon={FiClock} className="text-gray-400" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'relevant': return 'Relevant';
      case 'irrelevant': return 'Ikke relevant';
      case 'needs_clarification': return 'Trenger avklaring';
      case 'reviewed': return 'Vurdert';
      default: return 'Venter på vurdering';
    }
  };

  const getAnnotationTypeStyle = (type) => {
    switch (type) {
      case 'legal_point': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'question': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'redaction_note': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-[32px] border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden flex flex-col h-[700px]">
      {/* Header */}
      <div className="px-8 py-6 border-b dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-900/20">
            <SafeIcon icon={FiShield} size={20} />
          </div>
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest text-gray-900 dark:text-white">
              Bevis-Arbeidsområde
            </h3>
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-tight">
              Analyse og verifisering av saksdokumentasjon
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text"
              placeholder="Søk i bevis..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 transition-all outline-none"
            />
          </div>
          <select 
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-bold focus:ring-2 focus:ring-blue-500 transition-all outline-none"
          >
            <option value="all">Alle status</option>
            <option value="pending">Venter</option>
            <option value="relevant">Relevant</option>
            <option value="irrelevant">Ikke relevant</option>
          </select>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar: List */}
        <div className="w-80 border-r dark:border-gray-800 overflow-y-auto bg-gray-50/30 dark:bg-gray-900/30">
          {filteredEvidence.length === 0 ? (
            <div className="p-8 text-center">
              <SafeIcon icon={FiFileText} size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-xs font-bold text-gray-400 uppercase">Ingen bevis funnet</p>
            </div>
          ) : (
            <div className="divide-y dark:divide-gray-800">
              {filteredEvidence.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setSelectedItem(item);
                  }}
                  className={`w-full p-5 text-left transition-all flex gap-4 hover:bg-white dark:hover:bg-gray-800 ${
                    selectedItem?.id === item.id ? 'bg-white dark:bg-gray-800 ring-2 ring-inset ring-blue-500' : ''
                  }`}
                >
                  <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                    selectedItem?.id === item.id ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                  }`}>
                    <SafeIcon icon={FiFileText} size={20} />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-black text-gray-900 dark:text-white truncate uppercase tracking-tight">
                      {item.title}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      {getStatusIcon(item.review_status)}
                      <span className="text-[10px] font-black uppercase text-gray-400 tracking-wider">
                        {getStatusLabel(item.review_status)}
                      </span>
                    </div>
                    {item.redaction_status === 'completed' && (
                      <div className="flex items-center gap-1 mt-1">
                        <SafeIcon icon={FiScissors} size={10} className="text-purple-500" />
                        <span className="text-[9px] font-black uppercase text-purple-500 tracking-widest">Sladdet</span>
                      </div>
                    )}
                    <p className="text-[10px] text-gray-400 mt-2 font-bold uppercase">
                      Lagt til {format(new Date(item.created_at), 'dd.MM.yy')}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Main Content: Review Panel */}
        <div className="flex-1 overflow-y-auto p-8 bg-white dark:bg-gray-950">
          {selectedItem ? (
            <div className="max-w-3xl mx-auto space-y-8">
              {/* Top Meta */}
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-[10px] font-black bg-blue-100 text-blue-700 px-2 py-1 rounded uppercase tracking-widest">
                      Bevis ID: {selectedItem.id.slice(0, 8).toUpperCase()}
                    </span>
                    {selectedItem.file_hash && (
                      <span className="text-[10px] font-black bg-green-100 text-green-700 px-2 py-1 rounded uppercase tracking-widest flex items-center gap-1">
                        <SafeIcon icon={FiLock} size={10} /> Verifisert Integritet
                      </span>
                    )}
                    {selectedItem.needs_redaction && (
                      <span className="text-[10px] font-black bg-red-100 text-red-700 px-2 py-1 rounded uppercase tracking-widest flex items-center gap-1">
                        <SafeIcon icon={FiScissors} size={10} /> Krever Sladding
                      </span>
                    )}
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight">
                    {selectedItem.title}
                  </h2>
                </div>
                <div className="flex gap-2">
                  <button className="p-2.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-200 transition-colors">
                    <SafeIcon icon={FiDownload} />
                  </button>
                  <button 
                    onClick={() => setShowRedactionModal(true)}
                    className={`p-2.5 rounded-xl transition-all ${
                      selectedItem.redaction_status === 'completed'
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600'
                    } hover:bg-purple-50`}
                  >
                    <SafeIcon icon={FiScissors} />
                  </button>
                  <button className="p-2.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-200 transition-colors">
                    <SafeIcon icon={FiMoreVertical} />
                  </button>
                </div>
              </div>

              {/* Version Comparison Toggle (Integration) */}
              {selectedItem.redacted_file_url && (
                <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl w-fit">
                  <button
                    onClick={() => setPreviewMode('original')}
                    className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
                      previewMode === 'original' ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-500'
                    }`}
                  >
                    <SafeIcon icon={FiLayers} /> Original
                  </button>
                  <button
                    onClick={() => setPreviewMode('redacted')}
                    className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
                      previewMode === 'redacted' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-500'
                    }`}
                  >
                    <SafeIcon icon={FiScissors} /> Sladdet
                  </button>
                </div>
              )}

              {/* Redaction Info Alert */}
              {selectedItem.redaction_status === 'completed' && (
                <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-800/50 rounded-2xl p-4 flex items-start gap-4">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 rounded-lg">
                    <SafeIcon icon={FiScissors} />
                  </div>
                  <div>
                    <h4 className="text-xs font-black text-purple-900 dark:text-purple-100 uppercase tracking-widest">Dokumentet er sladdet</h4>
                    <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                      Sladdet av {selectedItem.redacted_by} den {format(new Date(selectedItem.redacted_at), 'dd.MM.yy')}.
                    </p>
                    <p className="text-[10px] text-purple-600/70 mt-1 italic font-medium">
                      "{selectedItem.redaction_notes}"
                    </p>
                  </div>
                </div>
              )}

              {/* Description & Preview Mock */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-6 border dark:border-gray-800">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">Beskrivelse</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium leading-relaxed">
                      {selectedItem.description || 'Ingen beskrivelse tilgjengelig.'}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-6 border dark:border-gray-800">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">Metadata</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-[11px]">
                        <span className="text-gray-400 font-bold uppercase">Lagt til av:</span>
                        <span className="text-gray-900 dark:text-white font-black">{selectedItem.added_by}</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-gray-400 font-bold uppercase">Dato:</span>
                        <span className="text-gray-900 dark:text-white font-black">
                          {format(new Date(selectedItem.created_at), 'PPP', { locale: nb })}
                        </span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-gray-400 font-bold uppercase">Kilde:</span>
                        <span className="text-gray-900 dark:text-white font-black uppercase">
                          {selectedItem.metadata?.source || 'Ukjent'}
                        </span>
                      </div>
                      <div className="pt-4 mt-4 border-t dark:border-gray-800">
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <input 
                            type="checkbox"
                            checked={selectedItem.needs_redaction}
                            onChange={(e) => handleUpdateReview(selectedItem.id, { needs_redaction: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-[11px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-tight group-hover:text-blue-600 transition-colors">
                            Inneholder personopplysninger
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`rounded-2xl border-2 border-dashed flex flex-col items-center justify-center p-8 text-center transition-all ${
                  previewMode === 'redacted' ? 'bg-purple-50 border-purple-200' : 'bg-gray-100 border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                }`}>
                  <SafeIcon icon={previewMode === 'redacted' ? FiScissors : FiEye} size={48} className={`${previewMode === 'redacted' ? 'text-purple-400' : 'text-gray-300'} mb-4`} />
                  <p className="text-sm font-black text-gray-400 uppercase tracking-widest">
                    {previewMode === 'redacted' ? 'Sladdet Versjon' : 'Forhåndsvisning'}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-2">
                    {previewMode === 'redacted' ? 'Revisjon klar for deling' : 'Klikk for å åpne filen i full størrelse'}
                  </p>
                  <button className={`mt-6 px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest shadow-sm transition-all ${
                    previewMode === 'redacted' ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                  }`}>
                    Åpne dokument
                  </button>
                </div>
              </div>

              {/* Workspace Tabs */}
              <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl w-fit">
                <button
                  onClick={() => setActiveTab('review')}
                  className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                    activeTab === 'review' ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-500'
                  }`}
                >
                  <SafeIcon icon={FiActivity} className="inline mr-2" /> Granskers Vurdering
                </button>
                <button
                  onClick={() => setActiveTab('annotations')}
                  className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all relative ${
                    activeTab === 'annotations' ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-500'
                  }`}
                >
                  <SafeIcon icon={FiEdit3} className="inline mr-2" /> Annoteringer
                  {annotations.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white text-[8px] flex items-center justify-center rounded-full border border-white">
                      {annotations.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Review Workspace Content */}
              {activeTab === 'review' ? (
                <div className="bg-blue-600 rounded-[28px] p-8 text-white shadow-xl shadow-blue-900/20">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-white/20 rounded-xl">
                      <SafeIcon icon={FiActivity} size={20} />
                    </div>
                    <h3 className="text-sm font-black uppercase tracking-widest">Granskers Vurdering</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest mb-3 text-blue-100">Status for bevis</label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { id: 'relevant', label: 'Relevant', icon: FiCheckCircle },
                            { id: 'irrelevant', label: 'Uvesentlig', icon: FiXCircle },
                            { id: 'needs_clarification', label: 'Avklaring', icon: FiAlertCircle },
                            { id: 'reviewed', label: 'Vurdert', icon: FiActivity }
                          ].map(opt => (
                            <button
                              key={opt.id}
                              onClick={() => handleUpdateReview(selectedItem.id, { review_status: opt.id })}
                              disabled={isSaving}
                              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border-2 ${
                                selectedItem.review_status === opt.id 
                                  ? 'bg-white text-blue-600 border-white shadow-lg' 
                                  : 'bg-blue-500/30 text-white border-transparent hover:bg-blue-500/50'
                              }`}
                            >
                              <SafeIcon icon={opt.icon} />
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest mb-3 text-blue-100">Viktighetsgrad (1-5)</label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map(level => (
                            <button
                              key={level}
                              onClick={() => handleUpdateReview(selectedItem.id, { importance_level: level })}
                              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                                selectedItem.importance_level >= level ? 'text-yellow-400' : 'text-blue-400'
                              }`}
                            >
                              <SafeIcon icon={FiStar} className={selectedItem.importance_level >= level ? 'fill-current' : ''} size={24} />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <label className="block text-[10px] font-black uppercase tracking-widest text-blue-100">Analyse og konklusjon</label>
                      <textarea 
                        rows={6}
                        defaultValue={selectedItem.analysis_notes}
                        onBlur={(e) => handleUpdateReview(selectedItem.id, { analysis_notes: e.target.value })}
                        placeholder="Skriv din vurdering av bevisets betydning for saken her..."
                        className="w-full bg-blue-500/30 border-2 border-blue-400/30 rounded-2xl p-4 text-sm text-white placeholder-blue-200 focus:outline-none focus:border-white transition-all resize-none"
                      />
                      <p className="text-[10px] text-blue-200 font-medium italic">
                        {isSaving ? 'Lagrer...' : 'Sist vurdert av ' + (selectedItem.reviewed_by || 'ingen') + ' ' + (selectedItem.reviewed_at ? format(new Date(selectedItem.reviewed_at), 'dd.MM HH:mm') : '')}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Annotations Layer */}
                  <div className="bg-white dark:bg-gray-900 rounded-3xl border-2 border-gray-100 dark:border-gray-800 p-8">
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
                          <SafeIcon icon={FiEdit3} />
                        </div>
                        <h3 className="text-sm font-black uppercase tracking-widest text-gray-900 dark:text-white">Annoteringslag</h3>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                        {annotations.length === 0 ? (
                          <div className="p-12 text-center border-2 border-dashed border-gray-100 dark:border-gray-800 rounded-2xl">
                            <SafeIcon icon={FiPlus} size={32} className="mx-auto text-gray-300 mb-4" />
                            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Ingen annoteringer</p>
                          </div>
                        ) : (
                          annotations.map((ann) => (
                            <motion.div 
                              key={ann.id}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              className={`p-5 rounded-2xl border-l-4 shadow-sm ${getAnnotationTypeStyle(ann.type)}`}
                            >
                              <div className="flex justify-between items-start mb-2">
                                <span className="text-[9px] font-black uppercase tracking-widest opacity-70">
                                  {ann.type.replace('_', ' ')}
                                </span>
                                <button 
                                  onClick={() => handleDeleteAnnotation(ann.id)}
                                  className="text-red-400 hover:text-red-600 transition-colors"
                                >
                                  <SafeIcon icon={FiTrash2} size={14} />
                                </button>
                              </div>
                              <p className="text-sm font-medium leading-relaxed">{ann.content}</p>
                              <div className="mt-4 flex items-center gap-2 text-[9px] font-bold opacity-60 uppercase tracking-widest">
                                <span>{ann.author_name}</span>
                                <span>•</span>
                                <span>{format(new Date(ann.created_at), 'dd.MM HH:mm')}</span>
                              </div>
                            </motion.div>
                          ))
                        )}
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6 border dark:border-gray-800 h-fit">
                        <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6">Ny Annotering</h4>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { id: 'observation', label: 'Obs', icon: FiEye },
                              { id: 'legal_point', label: 'Lov', icon: FiShield },
                              { id: 'question', label: 'Spm', icon: FiMessageSquare },
                              { id: 'redaction_note', label: 'Sladd', icon: FiScissors }
                            ].map(type => (
                              <button
                                key={type.id}
                                onClick={() => setNewAnnotation(prev => ({ ...prev, type: type.id }))}
                                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all border-2 ${
                                  newAnnotation.type === type.id 
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                                    : 'bg-white dark:bg-gray-800 text-gray-500 border-gray-100 dark:border-gray-700 hover:border-blue-500'
                                }`}
                              >
                                <SafeIcon icon={type.icon} />
                                {type.label}
                              </button>
                            ))}
                          </div>
                          <textarea 
                            rows={4}
                            value={newAnnotation.content}
                            onChange={(e) => setNewAnnotation(prev => ({ ...prev, content: e.target.value }))}
                            placeholder="Legg til din observasjon eller merknad..."
                            className="w-full px-4 py-4 rounded-xl bg-white dark:bg-gray-800 border-2 border-transparent focus:border-blue-500 text-sm outline-none transition-all resize-none"
                          />
                          <button 
                            onClick={handleAddAnnotation}
                            disabled={isSaving || !newAnnotation.content.trim()}
                            className="w-full py-3 bg-blue-600 text-white rounded-xl font-black text-[10px] uppercase tracking-[0.2em] shadow-lg shadow-blue-900/20 hover:bg-blue-700 transition-all disabled:opacity-50"
                          >
                            {isSaving ? 'Lagrer...' : 'Legg til merknad'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-3xl flex items-center justify-center text-gray-300 mb-6">
                <SafeIcon icon={FiSearch} size={40} />
              </div>
              <h3 className="text-lg font-black text-gray-400 uppercase tracking-widest">Velg et bevis</h3>
              <p className="text-sm text-gray-500 mt-2">Marker et bevis fra listen til venstre for å starte analysen.</p>
            </div>
          )}
        </div>
      </div>

      {/* Redaction Modal */}
      {showRedactionModal && selectedItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-gray-900 rounded-[32px] w-full max-w-xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800"
          >
            <div className="p-8 border-b dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-600 text-white rounded-xl">
                  <SafeIcon icon={FiScissors} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Håndter Sladding</h2>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">GDPR & Personvern</p>
                </div>
              </div>
              <button onClick={() => setShowRedactionModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <SafeIcon icon={FiIcons.FiX} className="text-xl text-gray-400" />
              </button>
            </div>

            <div className="p-8 space-y-6">
              <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/50 rounded-2xl p-4 text-xs text-amber-800 dark:text-amber-300 font-medium leading-relaxed">
                <SafeIcon icon={FiAlertCircle} className="inline mr-2" />
                Sladding er nødvendig når dokumenter skal deles med parter som ikke har autorisasjon til å se alle personopplysninger. Originalen vil fortsatt være tilgjengelig for granskere.
              </div>

              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-[24px] p-10 flex flex-col items-center justify-center text-center hover:border-blue-500 transition-colors cursor-pointer group">
                  <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <SafeIcon icon={FiUploadCloud} size={32} />
                  </div>
                  <h4 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">Last opp sladdet versjon</h4>
                  <p className="text-[10px] text-gray-400 mt-2 uppercase font-bold">PDF, JPG, PNG (Maks 10MB)</p>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Redaksjonsnotater</label>
                  <textarea 
                    id="redaction-notes"
                    rows={3}
                    defaultValue={selectedItem.redaction_notes}
                    placeholder="Beskriv hva som er fjernet (f.eks. 'Sladdet navn og kontonummer for ansatt X')..."
                    className="w-full px-6 py-4 rounded-2xl bg-gray-50 dark:bg-gray-800 border-2 border-transparent focus:border-purple-500 text-sm text-gray-900 dark:text-white outline-none transition-all resize-none"
                  />
                </div>
              </div>
            </div>

            <div className="p-8 bg-gray-50 dark:bg-gray-800/50 border-t dark:border-gray-800 flex justify-end gap-4">
              <button onClick={() => setShowRedactionModal(false)} className="px-8 py-4 text-gray-500 font-black text-xs uppercase tracking-widest">Avbryt</button>
              <button 
                onClick={() => {
                  const notes = document.getElementById('redaction-notes').value;
                  handleRedactionComplete(selectedItem.id, 'mock_redacted_url', notes);
                }}
                className="px-10 py-4 bg-purple-600 text-white font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-purple-700 transition-all shadow-xl shadow-purple-900/20"
              >
                Marker som sladdet
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}